/* Timezone helpers — searchable tz list + wall-time ↔ UTC conversion. */
export const TIMEZONES = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : ["UTC"];

export const browserTz = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return "UTC"; }
};

export function tzOffsetLabel(tz, at = new Date()) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "shortOffset" }).formatToParts(at);
    return parts.find((p) => p.type === "timeZoneName")?.value || "";
  } catch { return ""; }
}

let _tzOptionsCache = null;
export function tzOptions() {
  if (!_tzOptionsCache) {
    _tzOptionsCache = TIMEZONES.map((z) => ({ value: z, label: z.replace(/_/g, " "), hint: tzOffsetLabel(z) }));
  }
  return _tzOptionsCache;
}

/* Today's wall-clock date + JS weekday (sunday=0) in `tz`. */
export function todayInTz(tz) {
  try {
    const dtf = new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit", weekday: "short" });
    const p = Object.fromEntries(dtf.formatToParts(new Date()).map((x) => [x.type, x.value]));
    const jsDay = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(p.weekday);
    return { date: `${p.year}-${p.month}-${p.day}`, jsDay };
  } catch {
    const d = new Date();
    return { date: d.toISOString().slice(0, 10), jsDay: d.getUTCDay() };
  }
}

/* Convert a wall-clock date+time in `tz` to a UTC ISO string. */
export function zonedToUtcIso(dateStr, timeStr, tz) {
  const naive = new Date(`${dateStr}T${timeStr}:00Z`);
  if (isNaN(naive)) return null;
  try {
    const dtf = new Intl.DateTimeFormat("en-US", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
    const p = Object.fromEntries(dtf.formatToParts(naive).map((x) => [x.type, x.value]));
    const asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second);
    return new Date(2 * naive.getTime() - asUtc).toISOString();
  } catch {
    return new Date(`${dateStr}T${timeStr}`).toISOString();
  }
}

/* Convert a UTC ISO string to { date, time } wall-clock parts in `tz`. */
export function utcToZoned(iso, tz) {
  const d = new Date(iso);
  if (isNaN(d)) return { date: "", time: "" };
  try {
    const dtf = new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false });
    const p = Object.fromEntries(dtf.formatToParts(d).map((x) => [x.type, x.value]));
    return { date: `${p.year}-${p.month}-${p.day}`, time: `${p.hour === "24" ? "00" : p.hour}:${p.minute}` };
  } catch {
    return { date: iso.slice(0, 10), time: iso.slice(11, 16) };
  }
}
